import React, { useEffect, useState, useRef, useCallback } from 'react';
import Header from '../../components/Header/Header';
import { baseUrl } from '../../Urls';
import { FaEdit } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import '../Estimation/estimation.css';
import ExpandableText from '../../components/ExpandableText/ExpandableText';

function Estimation() {
  const [salesList, setSalesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();
  const tableContainerRef = useRef(null);
  const isEditingRef = useRef(false);

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${baseUrl}/api/sales`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      const processed = processEstimationData(data);
      setSalesList(processed);
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const handleEdit = (sale) => {
    sessionStorage.setItem("lastEditedEstimationId", sale._id);
    navigate('/sales', { state: { sale, from: 'estimation' } });
  };

  useEffect(() => {
    if (!salesList.length || !tableContainerRef.current) return;

    const editedId = sessionStorage.getItem("lastEditedEstimationId");

    if (editedId) {
      // Coming back from edit
      isEditingRef.current = true;
      setTimeout(() => {
        const rowElement = document.getElementById(`row-${editedId}`);
        if (rowElement) {
          rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
          rowElement.classList.add("highlight-row");
          setTimeout(() => rowElement.classList.remove("highlight-row"), 2000);
        }
        sessionStorage.removeItem("lastEditedEstimationId");
      }, 500);
    } else if (!isEditingRef.current) {
      // Default: scroll to bottom
      requestAnimationFrame(() => {
        const container = tableContainerRef.current;
        if (!container) return;
        container.scrollTop = container.scrollHeight;
      });
    }
  }, [salesList]);

  const estColumns = [
    { key: 'edit', label: 'Edit', isAction: true },
    { key: 'sNo', label: 'SNo' },
    { key: 'date', label: 'Enq Date' },
    { key: 'companyName', label: 'Company Name' },
    { key: 'productName', label: 'Product Name' },
    { key: 'addressLine1', label: 'Address' },
    { key: 'contactPerson', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'tel', label: 'Telephone' },
    { key: 'email', label: 'Email' },
    { key: 'scope', label: 'Scope', render: (sale) => <ExpandableText text={sale.scope} maxLines={2} /> },
    { key: 'source', label: 'Source' },
    { key: 'attachments', label: 'Lead Attachments', isAttachment: true },
    { key: 'assignedBy', label: 'Assigned By' },
    { key: 'assignedTo', label: 'Assigned To' },
    { key: 'note', label: 'Lead Note', render: (sale) => <ExpandableText text={sale.note} maxLines={2} /> },
    { key: 'estDate', label: 'Quotation Date', default: '-' },
    { key: 'quotationNo', label: 'Quotation No' },
    { key: 'estimator', label: 'Estimator' },
    { key: 'estAttachments', label: 'EST Sheet', isAttachment: true },
    { key: 'qutPdf', label: 'Quotation Proposal', isAttachment: true },
    { key: 'poStatus', label: 'Proposal Status', isStatus: true },
  ];

  const processEstimationData = (data) => {
    return data.map(item => {
      if (item.assignedBy && item.assignedTo) {
        return item;
      } else {
        return {
          sNo: item.sNo,
          _id: item._id
        };
      }
    });
  };

  return (
    <>
      <Header salesList={salesList} />
      <div className="container estimation-container" style={{ marginTop: "100px" }}>
        <h2 className='est-heading'>Estimation Page</h2>
        <div className="table-container-est mt-3" ref={tableContainerRef}>
          {loading ? (
            <div className="spinner-container">
              <div className="spinner"></div>
              <p>Loading estimation data...</p>
            </div>
          ) : (
            <table className="table table-bordered est-table table-hover" style={{ fontSize: "14px" }}>
              <thead>
                <tr>
                  {estColumns.map(col => <th key={col.key}>{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {salesList.length === 0 ? (
                  <tr>
                    <td colSpan="25" className="text-left text-dark">
                      No data found
                    </td>
                  </tr>
                ) : (
                  salesList.map(sale => (
                    <tr
                      key={sale._id}
                      id={`row-${sale._id}`}
                      onClick={() => setSelectedRow(selectedRow === sale._id ? null : sale._id)}
                      className={selectedRow === sale._id ? 'highlight-row' : ''}
                      style={{ cursor: "pointer" }}
                    >
                      {estColumns.map(col => {
                        if (col.isAction) {
                          return (
                            <td key={col.key}>
                              <FaEdit
                                style={{ marginLeft: "5px", color: "gray", cursor: "pointer" }}
                                onClick={() => handleEdit(sale)}
                              />
                            </td>
                          );
                        }
                        if (col.isAttachment) {
                          return (
                            <td key={col.key}>
                              {sale[col.key]?.map((link, idx) => (
                                <div key={idx}>
                                  <a href={link} target="_blank" rel="noopener noreferrer">
                                    View File {idx + 1}
                                  </a>
                                </div>
                              ))}
                            </td>
                          );
                        }
                        if (col.isCurrency) {
                          return (
                            <td key={col.key}>
                              {sale[col.key] ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(sale[col.key]) : ''}
                            </td>
                          );
                        }
                        if (col.isStatus) {
                          let displayStatus = sale.poStatus;
                          if (!sale.poStatus || sale.poStatus === 'InProgress' || sale.poStatus === 'pending') {
                            if (sale.qutPdf && sale.qutPdf.length > 0) {
                              displayStatus = 'Pending';
                            } else {
                              displayStatus = 'InProgress';
                            }
                          }

                          const badgeClass =
                            displayStatus === 'approved'
                              ? 'bg-success'
                              : displayStatus === 'rejected'
                                ? 'bg-danger'
                                : 'bg-warning text-dark';

                          return (
                            <td key={col.key}>
                              <span className={`badge ${badgeClass}`}>
                                {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                              </span>
                            </td>
                          );
                        }
                        if (col.render) {
                          return <td key={col.key}>{col.render(sale)}</td>;
                        }
                        return <td key={col.key}>{sale[col.key] ?? col.default ?? ''}</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>

  )
}

export default Estimation